import React, { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Button,
  TextField,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, isBefore, startOfDay, subDays, isAfter } from 'date-fns';
import { Task, CreateTaskDTO } from '../../services/api/todo.service';

export interface BoardColumn {
  id: string;
  title: string;
  taskIds: string[];
  color?: string;
  wipLimit?: number;
  collapsed?: boolean;
}

export type BoardColumns = Record<string, BoardColumn>;

export interface BoardConfig {
  columns: BoardColumns;
  columnOrder: string[];
  swimlanes?: 'none' | 'project' | 'assignee' | 'priority';
}

interface TaskBoardViewProps {
  tasks: Task[];
  boardConfig: BoardConfig;
  onTaskClick: (taskId: string) => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  onTaskCreate: (task: CreateTaskDTO, columnId: string) => void;
  onTaskDelete: (taskId: string) => void;
  onBoardConfigUpdate: (config: BoardConfig) => void;
  onTaskMove: (taskId: string, sourceColumn: string, destColumn: string, newIndex: number) => void;
  enableQuickAdd?: boolean;
  compactMode?: boolean;
}

// Sortable Task Card Component
const SortableTaskCard: React.FC<{
  task: Task;
  onTaskClick: (taskId: string) => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  onTaskDelete: (taskId: string) => void;
  compactMode?: boolean;
  isOverdue: boolean;
  isStarred: boolean;
  onToggleStar: (taskId: string) => void;
}> = ({ task, onTaskClick, onTaskUpdate, onTaskDelete, compactMode, isOverdue, isStarred, onToggleStar }) => {
  const sortableData = useSortable({ id: task.id });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = sortableData;

  // Debug logging removed - drag and drop is working!

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const theme = useTheme();

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent': return '#d32f2f';
      case 'high': return '#ed6c02';
      case 'medium': return theme.palette.info.main;
      default: return 'rgba(0,0,0,0.6)';
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={{
        ...style,
        marginBottom: 8,
        cursor: 'grab',
        border: isOverdue ? `2px solid #d32f2f` : '1px solid rgba(0,0,0,0.12)',
        touchAction: 'none',
        userSelect: 'none',
      }}
      onClick={(e) => {
        // Don't handle clicks if we're currently dragging or if it's a drag-related event
        if (e.defaultPrevented || isDragging) {
          return;
        }
        onTaskClick(task.id);
      }}
      {...attributes}
      {...listeners}
    >
      <CardContent style={{ padding: compactMode ? 12 : 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <Typography
            variant={compactMode ? 'body2' : 'body1'}
            style={{
              flex: 1,
              textDecoration: task.status === 'completed' ? 'line-through' : 'none',
              color: isOverdue ? '#d32f2f' : 'inherit'
            }}
          >
            {task.title}
          </Typography>
          <div style={{ display: 'flex', gap: 4 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onToggleStar(task.id);
              }}
            >
              {isStarred ?
                <StarIcon fontSize="small" color="warning" /> :
                <StarBorderIcon fontSize="small" />
              }
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (window.confirm(`Are you sure you want to delete "${task.title}"?`)) {
                  onTaskDelete(task.id);
                }
              }}
              style={{ color: '#d32f2f' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {!compactMode && task.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              marginBottom: 8
            }}
          >
            {task.description}
          </Typography>
        )}

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {task.priority && task.priority !== 'medium' && (
            <Chip
              icon={<FlagIcon />}
              label={task.priority}
              size="small"
              style={{
                backgroundColor: alpha(getPriorityColor(task.priority), 0.1),
                color: getPriorityColor(task.priority)
              }}
            />
          )}

          {task.due_date && (
            <Chip
              icon={<CalendarIcon />}
              label={format(new Date(task.due_date), 'MMM d')}
              size="small"
              color={isOverdue ? 'error' : 'default'}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Sortable Column Component
const SortableColumn: React.FC<{
  column: BoardColumn;
  columnId: string;
  tasks: Task[];
  onTaskClick: (taskId: string) => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  onTaskCreate: (task: CreateTaskDTO, columnId: string) => void;
  onTaskDelete: (taskId: string) => void;
  onColumnUpdate: (columnId: string, updates: Partial<BoardColumn>) => void;
  onToggleCollapse: (columnId: string) => void;
  onDeleteColumn?: (columnId: string) => void;
  enableQuickAdd?: boolean;
  compactMode?: boolean;
  starredTasks: Set<string>;
  onToggleStar: (taskId: string) => void;
}> = ({
  column,
  columnId,
  tasks,
  onTaskClick,
  onTaskUpdate,
  onTaskCreate,
  onTaskDelete,
  onColumnUpdate,
  onToggleCollapse,
  onDeleteColumn,
  enableQuickAdd,
  compactMode,
  starredTasks,
  onToggleStar,
}) => {
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(column.title);
  const [completedDaysBack, setCompletedDaysBack] = useState(7);

  // Make the column itself droppable
  const {
    setNodeRef,
    isOver
  } = useSortable({
    id: columnId,
    data: {
      type: 'column',
      column
    }
  });

  const isOverLimit = column.wipLimit && tasks.length > column.wipLimit;
  const isCollapsed = column.collapsed || false;

  // Filter completed tasks to only show recent ones
  const filteredTasks = React.useMemo(() => {
    if (column.title.toLowerCase() === 'completed' || column.title.toLowerCase() === 'done') {
      const cutoffDate = subDays(new Date(), completedDaysBack);
      return tasks.filter(task => {
        // If task has an updated_at date, use that; otherwise use created_at
        const taskDate = task.updated_at ? new Date(task.updated_at) : new Date(task.created_at);
        return isAfter(taskDate, cutoffDate);
      });
    }
    return tasks;
  }, [tasks, column.title, completedDaysBack]);

  const handleQuickAdd = () => {
    if (!quickAddTitle.trim()) return;

    // Map common column IDs to valid task statuses
    const columnToStatus: Record<string, Task['status']> = {
      'todo': 'pending',
      'to_do': 'pending',
      'pending': 'pending',
      'in_progress': 'in_progress',
      'in_review': 'blocked',
      'blocked': 'blocked',
      'done': 'completed',
      'completed': 'completed',
      'cancelled': 'cancelled'
    };

    // Try to map column ID to a valid status, fallback to using column ID as status if it's valid
    let status: Task['status'] = columnToStatus[columnId];

    // If no mapping exists, check if the columnId itself is a valid status
    const validStatuses: Task['status'][] = ['pending', 'in_progress', 'completed', 'blocked', 'cancelled'];
    if (!status && validStatuses.includes(columnId as Task['status'])) {
      status = columnId as Task['status'];
    }

    // Default to 'pending' if still no valid status
    if (!status) {
      status = 'pending';
    }

    const newTask: CreateTaskDTO = {
      title: quickAddTitle,
      status
    };

    onTaskCreate(newTask, columnId);
    setQuickAddTitle('');
    setShowQuickAdd(false);
  };

  const handleTitleSave = () => {
    if (editedTitle.trim() && editedTitle !== column.title) {
      onColumnUpdate(columnId, { title: editedTitle });
    }
    setIsEditingTitle(false);
  };

  const isOverdue = (task: Task) => {
    if (!task.due_date || task.status === 'completed') return false;
    return isBefore(new Date(task.due_date), startOfDay(new Date()));
  };

  return (
    <Paper
      ref={setNodeRef}
      style={{
        width: 300,
        minWidth: 300,
        height: 'calc(100vh - 350px)',
        maxHeight: 'calc(100vh - 350px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isOver
          ? alpha('#325BA8', 0.08)
          : alpha('#fff', 0.9),
        border: isOver ? '2px solid #325BA8' : '1px solid rgba(0,0,0,0.12)',
        transition: 'all 0.2s ease'
      }}
    >
      {/* Column header */}
      <div
        style={{
          padding: 16,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          backgroundColor: column.color ? alpha(column.color, 0.08) : undefined
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            {isEditingTitle ? (
              <TextField
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') {
                    setEditedTitle(column.title);
                    setIsEditingTitle(false);
                  }
                }}
                size="small"
                autoFocus
                style={{ flex: 1 }}
              />
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight="bold">
                  {column.title}
                </Typography>
                <IconButton size="small" onClick={() => setIsEditingTitle(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </>
            )}
            <Chip
              label={filteredTasks.length}
              size="small"
              color={isOverLimit ? 'error' : 'default'}
            />
            {column.wipLimit && (
              <Tooltip title={`WIP Limit: ${column.wipLimit}`}>
                <Typography variant="caption" color="text.secondary">
                  / {column.wipLimit}
                </Typography>
              </Tooltip>
            )}
            {(column.title.toLowerCase() === 'completed' || column.title.toLowerCase() === 'done') && (
              <Select
                size="small"
                value={completedDaysBack}
                onChange={(e) => setCompletedDaysBack(Number(e.target.value))}
                onClick={(e) => e.stopPropagation()}
                style={{ marginLeft: 8, minWidth: 80 }}
              >
                <MenuItem value={1}>1 day</MenuItem>
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={14}>14 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
                <MenuItem value={90}>90 days</MenuItem>
              </Select>
            )}
          </div>
          <div>
            <IconButton size="small" onClick={() => onToggleCollapse(columnId)}>
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </div>
        </div>
      </div>

      {/* Column content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          padding: 8,
          minHeight: isCollapsed ? 10 : 200,
          overflowY: isCollapsed ? 'hidden' : 'auto',
          overflowX: 'hidden',
          transition: 'all 0.3s'
        }}
      >
        {!isCollapsed && (
          <SortableContext
            id={columnId}
            items={filteredTasks.map(t => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <div style={{ minHeight: filteredTasks.length === 0 ? 100 : 'auto' }}>
              {filteredTasks.length === 0 ? (
                <div
                  style={{
                    height: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed rgba(0,0,0,0.12)',
                    borderRadius: 4,
                    color: 'rgba(0,0,0,0.6)',
                    fontSize: '0.875rem'
                  }}
                >
                  Drop tasks here
                </div>
              ) : (
                // Deduplicate tasks here as well to be extra safe
                Array.from(new Map(filteredTasks.map(task => [task.id, task])).values()).map((task) => (
                  <SortableTaskCard
                    key={task.id}
                    task={task}
                    onTaskClick={onTaskClick}
                    onTaskUpdate={onTaskUpdate}
                    onTaskDelete={onTaskDelete}
                    compactMode={compactMode}
                    isOverdue={isOverdue(task)}
                    isStarred={starredTasks.has(task.id)}
                    onToggleStar={onToggleStar}
                  />
                ))
              )}
            </div>
          </SortableContext>
        )}
      </div>

      {/* Quick add card */}
      {enableQuickAdd && !isCollapsed && (
        <div style={{ padding: 8 }}>
          {showQuickAdd ? (
            <Paper style={{ padding: 8 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter task title"
                value={quickAddTitle}
                onChange={(e) => setQuickAddTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleQuickAdd();
                  if (e.key === 'Escape') {
                    setQuickAddTitle('');
                    setShowQuickAdd(false);
                  }
                }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Button size="small" variant="contained" onClick={handleQuickAdd}>
                  Add
                </Button>
                <Button size="small" onClick={() => {
                  setQuickAddTitle('');
                  setShowQuickAdd(false);
                }}>
                  Cancel
                </Button>
              </div>
            </Paper>
          ) : (
            <Button
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => setShowQuickAdd(true)}
              style={{ justifyContent: 'flex-start' }}
            >
              Add a card
            </Button>
          )}
        </div>
      )}
    </Paper>
  );
};

const TaskBoardView: React.FC<TaskBoardViewProps> = ({
  tasks,
  boardConfig,
  onTaskClick,
  onTaskUpdate,
  onTaskCreate,
  onTaskDelete,
  onBoardConfigUpdate,
  onTaskMove,
  enableQuickAdd = true,
  compactMode = false
}) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [starredTasks, setStarredTasks] = useState<Set<string>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get task by ID
  const taskMap = useMemo(() => {
    const map = new Map<string, Task>();
    tasks.forEach(task => map.set(task.id, task));
    return map;
  }, [tasks]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    // handleDragOver - handles cross-column moves
    const { active, over } = event;
    if (!over) return;

    const activeTaskId = active.id as string;
    const overId = over.id as string;

    // Find which column the active task is in
    let sourceColumnId: string | null = null;
    let destColumnId: string | null = null;

    for (const [columnId, column] of Object.entries(boardConfig.columns)) {
      if (column.taskIds.includes(activeTaskId)) {
        sourceColumnId = columnId;
      }
    }

    // Find destination column
    // First check if we're over a column (using data.type)
    if (over.data?.current?.type === 'column') {
      destColumnId = overId;
    } else {
      // Otherwise check if overId is a column ID or a task in a column
      for (const [columnId, column] of Object.entries(boardConfig.columns)) {
        if (columnId === overId) {
          destColumnId = columnId;
        } else if (column.taskIds.includes(overId)) {
          destColumnId = columnId;
        }
      }
    }

    if (!sourceColumnId || !destColumnId || sourceColumnId === destColumnId) {
      return;
    }

    // Move task to new column
    const sourceColumn = boardConfig.columns[sourceColumnId];
    const destColumn = boardConfig.columns[destColumnId];

    // Remove task from source (avoiding duplicates)
    const sourceTaskIds = sourceColumn.taskIds.filter(id => id !== activeTaskId);
    // Remove any existing instance from destination to prevent duplicates
    const destTaskIds = destColumn.taskIds.filter(id => id !== activeTaskId);

    // If dropping on a column (empty or not), add to end
    if (boardConfig.columns[overId]) {
      destTaskIds.push(activeTaskId);
    } else {
      // Dropping on a task - insert before that task
      const overIndex = destTaskIds.indexOf(overId);
      if (overIndex >= 0) {
        destTaskIds.splice(overIndex, 0, activeTaskId);
      } else {
        destTaskIds.push(activeTaskId);
      }
    }

    const newConfig = {
      ...boardConfig,
      columns: {
        ...boardConfig.columns,
        [sourceColumnId]: { ...sourceColumn, taskIds: sourceTaskIds },
        [destColumnId]: { ...destColumn, taskIds: destTaskIds }
      }
    };

    onBoardConfigUpdate(newConfig);

    // Make the API call for cross-column moves right here since we have reliable column detection
    if (onTaskMove && sourceColumnId !== destColumnId) {
      const destTaskIds = newConfig.columns[destColumnId].taskIds;
      const newIndex = destTaskIds.indexOf(activeTaskId);

      console.log('🔄 handleDragOver: Making API call for cross-column move', {
        taskId: activeTaskId,
        fromColumn: sourceColumnId,
        toColumn: destColumnId,
        newIndex
      });

      onTaskMove(activeTaskId, sourceColumnId, destColumnId, newIndex);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    console.log('🎯 handleDragEnd called:', { active: event.active.id, over: event.over?.id });
    const { active, over } = event;

    if (!over || active.id === over.id) {
      console.log('🎯 handleDragEnd: No valid drop target or same position');
      setActiveId(null);
      return;
    }

    const activeTaskId = active.id as string;
    const overId = over.id as string;

    // Since handleDragOver already moved the task in the UI, we need to find
    // where the task WAS originally and where it IS now
    let sourceColumnId: string | null = null;
    let destColumnId: string | null = null;

    // Find current position of the task (after handleDragOver moved it)
    for (const [colId, column] of Object.entries(boardConfig.columns)) {
      if (column.taskIds.includes(activeTaskId)) {
        destColumnId = colId;
        break;
      }
    }

    // To find the original column, we need to check the task's actual status
    // from the tasks array, not the board config (which was already updated)
    const task = tasks.find(t => t.id === activeTaskId);
    if (task) {
      // Map task status to column ID - this is the original column
      for (const [colId, column] of Object.entries(boardConfig.columns)) {
        // Check if this column's title matches the task's current status
        const taskStatus = task.task_status_id || task.status;
        if (column.title.toLowerCase() === taskStatus.toLowerCase() ||
            colId === taskStatus ||
            column.id === taskStatus) {
          sourceColumnId = colId;
          break;
        }
      }
    }

    console.log('🎯 Column detection:', {
      sourceColumnId,
      destColumnId,
      overId,
      availableColumnIds: Object.keys(boardConfig.columns),
      columnTitles: Object.fromEntries(Object.entries(boardConfig.columns).map(([id, col]) => [id, col.title])),
      sameColumn: sourceColumnId === destColumnId
    });

    if (!sourceColumnId) {
      setActiveId(null);
      return;
    }

    // Handle cross-column move
    if (destColumnId && sourceColumnId !== destColumnId) {
      // Cross-column move - API call was already made in handleDragOver, just clean up
      console.log('🎯 handleDragEnd: Cross-column move completed (API call already made in handleDragOver)');
      setActiveId(null);
      return;
    }

    // Handle same-column reordering
    if (destColumnId && sourceColumnId === destColumnId) {
      const column = boardConfig.columns[sourceColumnId];
      const oldIndex = column.taskIds.indexOf(activeTaskId);
      const newIndex = column.taskIds.indexOf(overId);

      if (oldIndex !== newIndex && newIndex !== -1) {
        const newTaskIds = arrayMove(column.taskIds, oldIndex, newIndex);
        const newConfig = {
          ...boardConfig,
          columns: {
            ...boardConfig.columns,
            [sourceColumnId]: { ...column, taskIds: newTaskIds }
          }
        };
        onBoardConfigUpdate(newConfig);
        // Note: For same-column reordering, we don't call onTaskMove as the status doesn't change
      }
    }

    setActiveId(null);
  };

  const toggleColumnCollapse = (columnId: string) => {
    const updatedConfig = { ...boardConfig };
    updatedConfig.columns[columnId].collapsed = !updatedConfig.columns[columnId].collapsed;
    onBoardConfigUpdate(updatedConfig);
  };

  const handleColumnUpdate = (columnId: string, updates: Partial<BoardColumn>) => {
    const updatedConfig = { ...boardConfig };
    updatedConfig.columns[columnId] = { ...updatedConfig.columns[columnId], ...updates };
    onBoardConfigUpdate(updatedConfig);
  };

  const toggleStar = (taskId: string) => {
    const newStarred = new Set(starredTasks);
    if (newStarred.has(taskId)) {
      newStarred.delete(taskId);
    } else {
      newStarred.add(taskId);
    }
    setStarredTasks(newStarred);
    onTaskUpdate(taskId, { metadata: { starred: newStarred.has(taskId) } });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Board header with swimlane controls */}
      <div style={{ padding: 16, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Task Board</Typography>
          <FormControl size="small">
            <Select
              value={boardConfig.swimlanes || 'none'}
              onChange={(e: SelectChangeEvent) => {
                onBoardConfigUpdate({
                  ...boardConfig,
                  swimlanes: e.target.value as BoardConfig['swimlanes']
                });
              }}
            >
              <MenuItem value="none">No Swimlanes</MenuItem>
              <MenuItem value="project">By Project</MenuItem>
              <MenuItem value="assignee">By Assignee</MenuItem>
              <MenuItem value="priority">By Priority</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Board columns */}
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', minHeight: 0 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div style={{ display: 'flex', gap: 16, padding: 16, height: '100%' }}>
            {(boardConfig?.columnOrder || []).map(columnId => {
              const column = boardConfig.columns[columnId];
              if (!column) return null;

              // Deduplicate task IDs to prevent React key warnings
              const uniqueTaskIds = Array.from(new Set(column.taskIds));
              const columnTasks = uniqueTaskIds
                .map(id => taskMap.get(id))
                .filter(Boolean) as Task[];

              return (
                <SortableColumn
                  key={columnId}
                  column={column}
                  columnId={columnId}
                  tasks={columnTasks}
                  onTaskClick={onTaskClick}
                  onTaskUpdate={onTaskUpdate}
                  onTaskCreate={onTaskCreate}
                  onTaskDelete={onTaskDelete}
                  onColumnUpdate={handleColumnUpdate}
                  onToggleCollapse={toggleColumnCollapse}
                  enableQuickAdd={enableQuickAdd}
                  compactMode={compactMode}
                  starredTasks={starredTasks}
                  onToggleStar={toggleStar}
                />
              );
            })}

            {/* Add new status button */}
            <Paper
              style={{
                width: 300,
                minWidth: 300,
                padding: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '2px dashed rgba(0,0,0,0.12)',
                backgroundColor: 'transparent'
              }}
              onClick={() => {
                // Prompt for new status name
                const statusName = prompt('Enter new status name:');
                if (!statusName || !statusName.trim()) return;

                // Create status ID from name (lowercase, replace spaces with underscores)
                const statusId = statusName.toLowerCase().replace(/\s+/g, '_');

                // Check if status already exists
                if (boardConfig.columns[statusId]) {
                  alert('A status with this name already exists');
                  return;
                }

                const newColumn: BoardColumn = {
                  id: statusId,
                  title: statusName,
                  taskIds: [],
                  color: '#999999'
                };

                onBoardConfigUpdate({
                  ...boardConfig,
                  columns: {
                    ...boardConfig.columns,
                    [statusId]: newColumn
                  },
                  columnOrder: [...boardConfig.columnOrder, statusId]
                });
              }}
            >
              <Button startIcon={<AddIcon />} color="primary">
                Add New Status
              </Button>
            </Paper>
          </div>

          <DragOverlay>
            {activeId ? (
              <Card style={{ opacity: 0.8 }}>
                <CardContent>
                  <Typography>{taskMap.get(activeId as string)?.title}</Typography>
                </CardContent>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default TaskBoardView;
